import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { HiChevronLeft as HiChevronLeftSolid } from "react-icons/hi";
import { QuestionMarkCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import TerraConnect from "../../components/terraConnect";
import GarminConnect from "../../components/garminConnect";
import { userService } from "../../services/userService";
import { ActiveSubscription } from "../../services/stripeTypes";
import { UserData } from "../../types/user";
import Header from "../../components/layout/Header";
import TerraBackfill from "../../components/TerraBackfill";
import { deauthenticateTerraDevice } from "../../services/terraService";
import { deauthenticateGarminDevice } from "../../services/garminService";
import peakHeroImage from "../../assets/peakHero.jpg";
import { useSubscription } from "../../providers/SubscriptionProvider";
import { FEATURE_GATES } from "../../config/featureAccess";
import ComingSoon from "../../components/ComingSoon";
import TerraImage from "../../assets/Terra.webp";
import AppleHealthIcon from "../../assets/appleHealth.png";

// Wearable logos
import whoopLogo from "../../assets/wearable/whoop.png";
import ouraLogo from "../../assets/wearable/oura.png";
import garminLogo from "../../assets/wearable/garmin.png";
import corosLogo from "../../assets/wearable/coros.png";
import ultrahumanLogo from "../../assets/wearable/ultrahuman.png";
import fitbitLogo from "../../assets/wearable/fitbit.png";
import polarLogo from "../../assets/wearable/polar.png";
import zeppLogo from "../../assets/wearable/zepp.png";
import googleLogo from "../../assets/wearable/google.png";

// Terra Device interface for type safety
interface TerraDevice {
  id: string;
  resource: string;
  connectedAt: string;
  expired?: boolean;
  integration?: string;
}
type DevicePreferenceKey = "sleep" | "daily" | "menstruation";

type DevicePreferences = {
  sleep: string;
  daily: string;
  menstruation: string;
};

const DEFAULT_PREFS: DevicePreferences = {
  sleep: "DEFAULT",
  daily: "DEFAULT",
  menstruation: "DEFAULT",
};
const WearableSettings: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [userData, setUserData] = useState<UserData | null>(null);
  const [subscription, setSubscription] = useState<ActiveSubscription | null>(
    null,
  );
  // const [hasPremiumAccess, setHasPremiumAccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [disconnectError, setDisconnectError] = useState<string | null>(null);
  const [showPopupWarning, setShowPopupWarning] = useState(false);
  const [deletingDeviceId, setDeletingDeviceId] = useState<string | null>(null);
  const [deviceToDelete, setDeviceToDelete] = useState<TerraDevice | null>(
    null,
  );
  const [waitingForConnection, setWaitingForConnection] = useState(false);
  const [initialDeviceCount, setInitialDeviceCount] = useState<number | null>(
    null,
  );
  const {
    subscription: userSubscription,
    isPlus,
    isPremium,
    isPlatinum,
    refreshSubscription,
  } = useSubscription();
  const [showInfoDialog, setShowInfoDialog] = useState(false);
  const [showDeviceSelector, setShowDeviceSelector] = useState(false);
  const [showComingSoonModal, setShowComingSoonModal] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<string | null>(null);
  const [showAppleHealthMessage, setShowAppleHealthMessage] = useState(false);
  const [triggerTerraConnect, setTriggerTerraConnect] = useState(false);
  const activeDevices =
    (userData?.deviceIDs as TerraDevice[] | undefined)?.filter(
      (d) => !d.expired,
    ) ?? [];

  const deviceOptions = activeDevices.map((d) => d.resource.toUpperCase());

  // Apple Health (via Terra's bridging app) is being phased out for new
  // connections — it's confusing alongside the native PeakApple HealthKit
  // integration and the Terra Avengers bridging app is unreliable. Users who
  // already connected this way keep working normally; we just stop offering
  // the entry point to anyone who hasn't.
  const hasExistingAppleDevice = activeDevices.some(
    (d) => d.resource.toUpperCase() === "APPLE",
  );

  const [prefs, setPrefs] = useState<DevicePreferences>(DEFAULT_PREFS);
  const [savingPrefs, setSavingPrefs] = useState(false);
  // Fetch user data
  const fetchData = async () => {
    if (!currentUser?.uid) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const [{ userData }] = await Promise.all([
        userService.getUserProfile(currentUser.uid),
        // userService.canAccessPremium(currentUser.uid),
      ]);
      setUserData(userData);
      await refreshSubscription();
      setSubscription(userSubscription);
      // setHasPremiumAccess(premiumAccess);
    } catch (error) {
      console.error("Error fetching data:", error);
      setError(error instanceof Error ? error.message : "Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };
  // All users can access wearable settings (1 device for Free/Plus, 3 for Premium)
  // Removed premium-only redirect
  useEffect(() => {
    if (!userData) return;

    let updated: DevicePreferences = {
      ...DEFAULT_PREFS,
      ...(userData.devicePreferences || {}),
    };

    let changed = false;

    // ensure each pref is valid
    (Object.keys(updated) as DevicePreferenceKey[]).forEach((key) => {
      const value = updated[key];

      if (value !== "DEFAULT" && !deviceOptions.includes(value)) {
        updated[key] = "DEFAULT";
        changed = true;
      }
    });

    // if prefs missing initially
    if (!userData.devicePreferences) {
      changed = true;
    }

    setPrefs(updated);

    if (changed) {
      if (!currentUser?.uid) return;
      userService.updateDevicePreferences(currentUser.uid, updated);
    }
  }, [userData, deviceOptions.join(",")]);

  useEffect(() => {
    fetchData();
  }, [currentUser?.uid]);

  // Listen for messages from the connection result popup
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Verify the message is from our origin
      if (event.origin !== window.location.origin) {
        return;
      }

      // Check if this is a Terra connection result message
      if (event.data?.type === "TERRA_CONNECTION_RESULT") {
        console.log("Received Terra connection result:", event.data);

        // Refresh the device list
        fetchData();
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [currentUser?.uid]);

  // Poll for device connection when waiting
  useEffect(() => {
    if (!waitingForConnection || initialDeviceCount === null) {
      return;
    }

    console.log(
      `Polling for device connection. Initial count: ${initialDeviceCount}`,
    );

    const pollInterval = setInterval(async () => {
      try {
        const { userData: freshUserData } = await userService.getUserProfile(
          currentUser?.uid || "",
        );

        const currentDeviceCount = freshUserData?.deviceIDs
          ? (freshUserData.deviceIDs as TerraDevice[]).filter(
              (device: TerraDevice) => !device.expired,
            ).length
          : 0;

        console.log(
          `Current device count: ${currentDeviceCount}, Initial: ${initialDeviceCount}`,
        );

        // Check if device count has increased
        if (currentDeviceCount > initialDeviceCount) {
          console.log("New device detected! Stopping polling.");
          setWaitingForConnection(false);
          setInitialDeviceCount(null);

          // Refresh the full data to update UI
          await fetchData();
        }
      } catch (error) {
        console.error("Error polling for device:", error);
      }
    }, 1000); // Poll every second

    // Stop polling after 2 minutes (timeout)
    const timeout = setTimeout(() => {
      console.log("Polling timeout reached");
      setWaitingForConnection(false);
      setInitialDeviceCount(null);
    }, 120000); // 2 minutes

    return () => {
      clearInterval(pollInterval);
      clearTimeout(timeout);
    };
  }, [waitingForConnection, initialDeviceCount, currentUser?.uid]);

  // Handle popup blocked detection from TerraConnect
  const handlePopupBlocked = useCallback(() => {
    setShowPopupWarning(true);
    // Hide warning after 10 seconds
    setTimeout(() => setShowPopupWarning(false), 10000);
  }, []);

  // Handle connection start - begin polling
  const handleConnectionStart = useCallback(() => {
    console.log("Connection started, beginning to poll for new device");

    // Calculate current device count
    const currentDeviceCount = userData?.deviceIDs
      ? (userData.deviceIDs as TerraDevice[]).filter(
          (device: TerraDevice) => !device.expired,
        ).length
      : 0;

    setInitialDeviceCount(currentDeviceCount);
    setWaitingForConnection(true);
  }, [userData]);

  // Callback to refresh user data when Terra connection is updated
  const handleTerraConnectionUpdate = useCallback(() => {
    fetchData();
  }, [currentUser?.uid]); // Add currentUser?.uid as dependency since fetchData depends on it

  // Auto-trigger Terra Connect when triggerTerraConnect is set
  useEffect(() => {
    if (triggerTerraConnect) {
      // Find and click the Terra Connect button
      const timer = setTimeout(() => {
        const terraButton = document.querySelector(
          ".terra-connect button",
        ) as HTMLButtonElement;
        if (terraButton) {
          terraButton.click();
        }
        setTriggerTerraConnect(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [triggerTerraConnect]);

  // Handle device deletion
  const handleDeleteDevice = useCallback(
    async (device: TerraDevice) => {
      if (!currentUser?.uid) return;

      setDeletingDeviceId(device.id);
      setDisconnectError(null);

      try {
        // Every Garmin device goes to the Garmin callable: it decides direct
        // vs Terra from server-only data and hands Terra devices to Terra.
        if (device.resource === "GARMIN") {
          await deauthenticateGarminDevice(device.id);
        } else {
          await deauthenticateTerraDevice(device.id);
        }

        // Refresh the data to show updated device list
        await fetchData();

        // Clear the delete confirmation dialog
        setDeviceToDelete(null);
      } catch (error) {
        console.error("Error deleting device:", error);
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to disconnect device";

        // Show user-friendly error message
        setDisconnectError(errorMessage);

        // Always try to refresh to see if the device was marked expired locally
        try {
          const { userData: freshUserData } = await userService.getUserProfile(
            currentUser.uid,
          );
          const deviceStillExists = freshUserData?.deviceIDs
            ? (freshUserData.deviceIDs as TerraDevice[]).some(
                (d: TerraDevice) => d.id === device.id && !d.expired,
              )
            : false;

          if (!deviceStillExists) {
            setDeviceToDelete(null);
            await fetchData();
            setDisconnectError(null); // Clear error since it actually worked
          }
        } catch (refreshError) {
          console.error("Error checking device status:", refreshError);
        }

        // Auto-hide error after 10 seconds
        setTimeout(() => setDisconnectError(null), 10000);
      } finally {
        setDeletingDeviceId(null);
      }
    },
    [currentUser?.uid],
  );

  // Show delete confirmation dialog
  const confirmDeleteDevice = useCallback((device: TerraDevice) => {
    setDeviceToDelete(device);
  }, []);

  // Cancel delete
  const cancelDelete = useCallback(() => {
    setDeviceToDelete(null);
  }, []);

  // Check user subscription tier for device limits
  // const subscriptionTypeLower = subscription?.subscriptionType
  //   ?.toLowerCase()
  //   .trim();
  // const isPlatinumUser = subscriptionTypeLower === "platinum";

  // Device limits by tier:
  // - Free/Default: 1 device
  // - Plus: 2 devices
  // - Premium/Platinum/Coach: 3 devices
  const maxDevices = isPremium ? 3 : isPlus ? 2 : 1;

  const connectedDeviceCount = userData?.deviceIDs
    ? (userData.deviceIDs as TerraDevice[]).filter(
        (device: TerraDevice) => !device.expired,
      ).length
    : 0;

  // Debug logging
  console.log("🔍 WearableSettings Debug:", {
    subscription,
    subscriptionType: subscription?.subscriptionType,
    isPlatinum,
    isPremium,
    maxDevices,
    connectedDeviceCount,
  });

  //device preferences

  const handleSavePrefs = async () => {
    try {
      setSavingPrefs(true);

      if (!currentUser?.uid) return;
      await userService.updateDevicePreferences(currentUser.uid, prefs);
    } finally {
      setSavingPrefs(false);
    }
  };

  if (loading) {
    return (
      <div className="container max-w-2xl mx-auto py-8 px-4">
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container max-w-2xl mx-auto py-8 px-4">
        <div className="card shadow-lg p-8 mt-8 text-center">
          <h3 className="text-lg font-semibold text-red-600 mb-2">
            Error Loading Wearable Data
          </h3>
          <p className="text-base-content/70 mb-6">{error}</p>
          <button
            className="btn btn-primary"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full pt-16">
      <Header />
      <div className="container max-w-2xl mx-auto py-8 px-8">
        <div className="flex items-center justify-between pb-4">
          <div className="flex">
            <button
              onClick={() => navigate("/settings")}
              className="flex items-center p-2 bg-base-300 rounded-full hover:bg-base-300 transition-colors"
            >
              <HiChevronLeftSolid className="h-6 w-6 text-base-content" />
            </button>
            <h1 className="text-3xl font-semibold text-base-content ml-4">
              Wearables
            </h1>
          </div>
          <button
            onClick={() => setShowInfoDialog(true)}
            className="ml-2 p-1 text-base-content/60 hover:text-base-content/80 transition-colors"
            aria-label="Information"
          >
            <QuestionMarkCircleIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Connection Section */}
        <div className="bg-base-100 rounded-lg shadow-sm border border-base-300 p-6 mb-4">
          <div className="">
            <div className="flex-1">
                            <div className="flex items-center justify-between mb-2">
                <h2 className="text-lg font-semibold text-base-content">
                  Available Devices
                </h2>
                <div className="">
                    <button
                      onClick={() => {
                        // Check device limit before opening device selector
                        if (
                          maxDevices !== undefined &&
                          connectedDeviceCount >= maxDevices
                        ) {
                          setShowComingSoonModal(true);
                          return;
                        }
                        setShowDeviceSelector(true);
                      }}
                      className="min-w-fit whitespace-nowrap rounded-xl px-4 py-2 flex items-center gap-2 text-sm bg-primary"
                    >
                    <span className="text-white font-grotesk">
                      Add Connection
                    </span>
                    <svg
                      className="w-4 h-4 flex-shrink-0"
                      fill="none"
                      stroke="white"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                      />
                    </svg>
                  </button>
                </div>
              </div>
              <p className="text-sm text-base-content/70 mb-4">
                Whoop, Garmin, Oura, Apple Health, Coros, Ultrahuman,
                Fitbit, Polar, Zepp & Google Fit
              </p>
              {maxDevices !== undefined && (
                <span
                  className={`text-sm font-medium bg-base-200 px-2 py-2 ${
                    connectedDeviceCount >= maxDevices
                      ? "text-red-600"
                      : "text-base-content/70"
                  }`}
                >
                  <strong>{connectedDeviceCount}</strong> / {maxDevices} devices
                  connected
                </span>
              )}
            </div>
          </div>

          {/* Waiting for connection indicator */}
          {waitingForConnection && (
            <div className="mt-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg
                    className="animate-spin h-5 w-5 text-blue-500"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-blue-700 dark:text-blue-400 dark:text-blue-300 font-medium">
                    Waiting for connection...
                  </p>
                  <p className="text-xs text-blue-700 dark:text-blue-400 mt-0.5">
                    Complete the connection process in the popup window
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Device limit info */}
          {isPlatinum && connectedDeviceCount > 0 && (
            <div className="mt-4 p-3 bg-gradient-to-r from-yellow-50 to-amber-50 border border-yellow-200 rounded-lg">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-yellow-500"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.293l-3-3a1 1 0 00-1.414-1.414L6 8.586 4.707 7.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4a1 1 0 000-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-yellow-700 dark:text-yellow-400 dark:text-yellow-300">
                    <span className="font-semibold">Platinum Member:</span>{" "}
                    Unlimited device connections available.
                  </p>
                  <p className="text-xs text-yellow-700 dark:text-yellow-400 mt-1">
                    Connect as many devices as you need with your Platinum
                    account.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Device limit info - Premium 3 devices */}
          {/* {isPremium && !isPlatinum && connectedDeviceCount > 0 && (
            <div className="mt-4 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-blue-500"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.293l-3-3a1 1 0 00-1.414-1.414L6 8.586 4.707 7.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4a1 1 0 000-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                {isPremium && (
                  <div className="ml-3">
                    <p className="text-sm text-blue-700 dark:text-blue-400 dark:text-blue-300">
                      <span className="font-semibold">Premium Member:</span>{" "}
                      Connect up to 3 devices.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )} */}

          {/* Hidden TerraConnect component - triggered programmatically */}
          {triggerTerraConnect && (
            <div className="hidden">
              <TerraConnect
                redirectEndpoint="/connectionresult"
                onConnectionUpdate={handleTerraConnectionUpdate}
                onPopupBlocked={handlePopupBlocked}
                onConnectionStart={handleConnectionStart}
                connectedDeviceCount={connectedDeviceCount}
                maxDevices={maxDevices}
                // Whether Apple Health is offered in the widget this opens
                // is decided server-side (generateTerraWidgetSession) from
                // the user's own device records, so it's correct regardless
                // of which button triggered this shared hidden instance —
                // the "Other Devices" picker or the Apple Health "Done,
                // proceed to next step" button for existing Apple users.
              />
            </div>
          )}

          {/* Popup blocked warning */}
          {showPopupWarning && (
            <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-amber-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-amber-700 dark:text-amber-400 dark:text-amber-300">
                    Pop ups disabled - Device connections may not work properly.
                    Please allow popups for this site in your browser settings.
                  </p>
                  <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                    On iOS: Settings → Safari → Block Pop-ups (turn off)
                  </p>
                </div>
              </div>
            </div>
          )}
          {/* Connected Devices Section */}
          <div className="mt-8">
            <h2 className="text-lg font-semibold text-base-content mb-4">
              Connected Devices
            </h2>

            {/* Disconnect Error Banner */}
            {disconnectError && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-red-400"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3 flex-1">
                    <p className="text-sm text-red-700 dark:text-red-400">
                      {disconnectError}
                    </p>
                  </div>
                  <button
                    onClick={() => setDisconnectError(null)}
                    className="flex-shrink-0 ml-2 text-red-500 hover:text-red-700"
                  >
                    <svg
                      className="h-4 w-4"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            )}

            {userData?.deviceIDs && userData.deviceIDs.length > 0 ? (
              <div className="space-y-3">
                {(userData.deviceIDs as TerraDevice[])
                  .filter((device: TerraDevice) => !device.expired) // Only show active devices
                  .map((device: TerraDevice, index: number) => (
                    <div
                      key={`${device.id}-${index}`}
                      className="p-4 bg-base-200 border border-base-300 rounded-lg"
                    >
                      <div className="flex items-center space-x-4">
                        {/* Device Info */}
                        <div className="w-full">
                          <div className="flex justify-between w-full">
                            <div className="flex space-x-2">
                              <p className="font-bold text-base-content">
                                {device.resource.charAt(0) +
                                  device.resource.slice(1).toLowerCase()}{" "}
                              </p>
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/20 text-green-700 dark:text-green-400">
                                Active
                              </span>
                            </div>
                            <button
                              onClick={() => confirmDeleteDevice(device)}
                              disabled={deletingDeviceId === device.id}
                              className="px-3 py-1 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-500/10 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {deletingDeviceId === device.id ? (
                                <span className="flex items-center">
                                  <svg
                                    className="animate-spin h-4 w-4 mr-1"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                  >
                                    <circle
                                      className="opacity-25"
                                      cx="12"
                                      cy="12"
                                      r="10"
                                      stroke="currentColor"
                                      strokeWidth="4"
                                    ></circle>
                                    <path
                                      className="opacity-75"
                                      fill="currentColor"
                                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    ></path>
                                  </svg>
                                  Removing...
                                </span>
                              ) : (
                                "Disconnect"
                              )}
                            </button>
                          </div>
                          <p className="text-sm text-base-content/60">
                            Connected:{" "}
                            {new Date(device.connectedAt).toLocaleString()}
                          </p>
                          <p className="text-xs text-base-content/50">
                            Device ID: {device.id}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <div className="">
                <h3 className="text-md font-medium text-base-content/60 font-grotesk">
                  No devices connected...
                </h3>
              </div>
            )}
          </div>
        </div>
        {/* Historical Data Sync */}
        {activeDevices.length > 0 && (
          <div className="bg-base-100 rounded-lg shadow-sm border border-base-300 p-6 mb-4">
            <TerraBackfill />
          </div>
        )}

        {activeDevices.length > 1 && (
          <div className="bg-base-100 rounded-lg shadow-sm border border-base-300 p-6 mb-4">
            {/* Device Preferences Section */}
            <div>
              <h2 className="text-lg font-semibold text-base-content mb-">
                Device Preferences
              </h2>
              <p className="text-sm text-base-content/60 mb-4">
                Assign a wearable to a preferred data collection type. Sometimes
                your wearables can collect the same data. Save your preferences
                below to ensure you have the right data applied to the right
                data collections in Peak.
              </p>

              <div className="space-y-4">
                {(["sleep", "daily", "menstruation"] as DevicePreferenceKey[])
                  .filter(
                    (key) =>
                      key !== "menstruation" ||
                      userData?.gender?.toLowerCase() !== "male",
                  )
                  .map((key) => {
                    const labelMap: Record<DevicePreferenceKey, string> = {
                      sleep: "Sleep & Health",
                      daily: "Daily Activities",
                      menstruation: "Female Cycle",
                    };
                    return (
                      <div
                        key={key}
                        className="flex items-center justify-between"
                      >
                        <label className="text-sm font-medium text-base-content/80">
                          {labelMap[key]}
                        </label>

                        <select
                          value={prefs[key]}
                          onChange={(e) =>
                            setPrefs((prev) => ({
                              ...prev,
                              [key]: e.target.value,
                            }))
                          }
                          className="border border-base-300 rounded-md px-3 py-1.5 text-sm  focus:outline-none bg-base-100"
                        >
                          <option value="DEFAULT">Default</option>
                          {deviceOptions.map((device) => (
                            <option key={device} value={device}>
                              {device.charAt(0) + device.slice(1).toLowerCase()}
                            </option>
                          ))}
                        </select>
                      </div>
                    );
                  })}

                <div className="pt-2 flex justify-end">
                  <button
                    onClick={handleSavePrefs}
                    disabled={savingPrefs}
                    className="min-w-fit text-white whitespace-nowrap rounded-md px-4 py-2 flex items-center gap-2 text-sm bg-neutral hover:bg-neutral/90 disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {savingPrefs ? (
                      <>
                        <span className="loading loading-spinner loading-xs text-blue-600"></span>
                        Saving...
                      </>
                    ) : (
                      "Save Preferences"
                    )}
                  </button>
                  {/* className={`min-w-fit whitespace-nowrap rounded-md px-4 py-2 flex items-center gap-2 text-sm ${
                  maxDevices !== undefined && connectedDeviceCount >= maxDevices
                    ? "bg-base-content/40 cursor-not-allowed"
                    : "bg-neutral hover:bg-neutral/90"
                }`} */}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        {deviceToDelete && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-base-100 rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-base-content mb-3">
                Disconnect Device?
              </h3>
              <p className="text-base-content/70 mb-2">
                Are you sure you want to disconnect{" "}
                <span className="font-semibold">
                  {deviceToDelete.resource.charAt(0) +
                    deviceToDelete.resource.slice(1).toLowerCase()}{" "}
                  Health
                </span>
                ?
              </p>
              <p className="text-sm text-base-content/60 mb-6">
                This will stop syncing data from this device. You can reconnect
                it later if needed.
              </p>
              <div className="flex space-x-3 justify-end">
                <button
                  onClick={cancelDelete}
                  disabled={deletingDeviceId === deviceToDelete.id}
                  className="px-4 py-2 text-sm font-medium text-base-content/80 bg-base-200 hover:bg-base-300 rounded-md transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDeleteDevice(deviceToDelete)}
                  disabled={deletingDeviceId === deviceToDelete.id}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deletingDeviceId === deviceToDelete.id
                    ? "Disconnecting..."
                    : "Disconnect"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Information Dialog */}
        {showInfoDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-base-100 rounded-lg max-w-lg w-full max-h-[80vh] overflow-y-auto">
              <div className="p-8">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-grotesk font-semibold text-base-content">
                    Wearable Data
                  </h3>
                  <button
                    onClick={() => setShowInfoDialog(false)}
                    className="p-1 rounded-full hover:bg-base-200 transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5 text-base-content/60" />
                  </button>
                </div>

                <div className="space-y-4 text-base-content/80">
                  <div>
                    <p className="text-lg font-grotesk pb-4">
                      Connecting a wearable device enhances the user experience
                      and insights provided. Here are the core metrics used in
                      the Peak Score algorithm and data displays.
                    </p>

                    <p>- Resting Heart Rate (RHR)</p>
                    <p>- Heart Rate Variability (HRV)</p>
                    <p>- Respiratory Rate (RR)</p>
                    <p>- Sleep duration and phase timeline</p>
                    <p>- Activity Data (GPS, Heart Rates & Duration)</p>
                    <p>- Steps</p>
                  </div>
                  <div>
                    <p className="text-sm text-base-content/60 italic">
                      You can disconnect devices at any time. Disconnecting will
                      stop new data from syncing, but your historical data will
                      remain. You can reconnect the same device later if needed.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Device Selector Modal */}
        {showDeviceSelector && !showAppleHealthMessage && (
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-base-100 rounded-lg max-w-md w-full">
              <div className="p-8">
                <div className="flex justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-grotesk font-bold text-base-content text-left">
                      Select Device Type
                    </h3>
                    <p className="text-xs text-base-content/60">
                      to initiate correct connection process
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setShowDeviceSelector(false);
                      setSelectedDevice(null);
                    }}
                    className="p-1 rounded-full hover:bg-base-200 transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5 text-base-content/60" />
                  </button>
                </div>

                {/* Other Devices Button */}
                <button
                  onClick={() => {
                    // Check device limit before proceeding
                    if (
                      maxDevices !== undefined &&
                      connectedDeviceCount >= maxDevices
                    ) {
                      setShowComingSoonModal(true);
                      return;
                    }
                    setSelectedDevice("OURA");
                    setShowDeviceSelector(false);
                    // Trigger Terra Connect for non-Apple devices
                    setTriggerTerraConnect(true);
                  }}
                  className="w-full p-4 mb-4 border-2 text-left border-base-300 rounded-lg hover:border-base-content/60 transition-all items-center gap-3"
                >
                  <div className="flex flex-col gap-3">
                    <div className="flex flex-wrap gap-2">
                      <img
                        src={whoopLogo}
                        alt="Whoop"
                        className="h-12 w-auto object-contain opacity-70"
                      />
                      <img
                        src={ouraLogo}
                        alt="Oura"
                        className="h-12 w-auto object-contain opacity-70"
                      />
                      <img
                        src={corosLogo}
                        alt="Coros"
                        className="h-12 w-auto object-contain opacity-70"
                      />
                      <img
                        src={ultrahumanLogo}
                        alt="Ultrahuman"
                        className="h-12 w-auto object-contain opacity-70"
                      />
                      <img
                        src={fitbitLogo}
                        alt="Fitbit"
                        className="h-12 w-auto object-contain opacity-70"
                      />
                      <img
                        src={polarLogo}
                        alt="Polar"
                        className="h-12 w-auto object-contain opacity-70"
                      />
                      <img
                        src={zeppLogo}
                        alt="Zepp"
                        className="h-12 w-auto object-contain opacity-70"
                      />
                      <img
                        src={googleLogo}
                        alt="Google Fit"
                        className="h-12 w-auto object-contain opacity-70"
                      />
                    </div>
                  </div>
                </button>

                <p className="pb-4 text-center">or</p>

                <div className="space-y-2">
                  {/* Garmin Button — connects direct, not via Terra */}
                  <GarminConnect
                    iconSrc={garminLogo}
                    connectedDeviceCount={connectedDeviceCount}
                    maxDevices={maxDevices}
                    onConnectionStart={() => {
                      setShowDeviceSelector(false);
                      handleConnectionStart();
                    }}
                    onPopupBlocked={handlePopupBlocked}
                  />

                  {/* Apple Health (via Terra) — hidden for new connections,
                      see hasExistingAppleDevice above */}
                  {hasExistingAppleDevice && (
                    <button
                      onClick={() => {
                        // Check device limit before proceeding
                        if (
                          maxDevices !== undefined &&
                          connectedDeviceCount >= maxDevices
                        ) {
                          setShowComingSoonModal(true);
                          return;
                        }
                        setSelectedDevice("APPLE");
                        setShowAppleHealthMessage(true);
                      }}
                      className="w-full p-4 border-2 bg-base-300 text-center border-base-300 rounded-lg hover:border-base-content/60 hover:bg-base-200 transition-all items-center gap-3"
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex items-center">
                          <img
                            src={AppleHealthIcon}
                            alt="Apple Health"
                            className="w-10 h-10 object-contain rounded-2xl shadow-lg"
                          />
                          <span className="text-lg font-grotesk text-base-content pl-4">
                            My device is an <strong>Apple Watch</strong>
                          </span>
                        </div>
                      </div>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Apple Health Message Modal */}
        {showAppleHealthMessage && (
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-base-100 rounded-lg max-w-md w-full">
              <div className="p-8">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-grotesk font-semibold text-base-content">
                    Apple Health Connection
                  </h3>

                  <button
                    onClick={() => {
                      setShowAppleHealthMessage(false);
                      setShowDeviceSelector(false);
                      setSelectedDevice(null);
                    }}
                    className="p-1 rounded-full hover:bg-base-200 transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5 text-base-content/60" />
                  </button>
                </div>

                <div className="space-y-4">
                  <p className="text-md text-base-content">
                    Download <strong>Terra Avengers</strong> from the app store
                    to sync your Apple Device data.
                  </p>

                  <div className="flex items-center gap-4 p-4 rounded-md border-2  border-base-300">
                    <img
                      src={TerraImage}
                      alt="Terra Avengers App"
                      className="w-20 h-20 rounded-lg object-covr"
                    />
                    <div className="">
                      <h3>Terra Avengers</h3>
                      <p className="text-xs">
                        Required to connect Apple Health data
                      </p>
                      <div className="pt-2">
                        <button
                          onClick={() =>
                            window.open(
                              "https://apps.apple.com/au/app/terra-avengers/id6449848186",
                              "_blank",
                            )
                          }
                          className="bg-neutral rounded-md font-grotesk text-white py-1.5 px-2.5 text-sm hover:bg-neutral/90 transition-colors"
                        >
                          Download
                        </button>
                      </div>
                    </div>
                  </div>

                  <p className="text-xs text-base-content/80">
                    Terra Avengers is a bridging app which allows Peak to
                    connect your Apple Device data. Peak is a special web app
                    and cannot connect directly to the iOS Health app at this
                    stage.
                  </p>

                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={() => {
                        setShowAppleHealthMessage(false);
                        setShowDeviceSelector(false);
                        setSelectedDevice(null);
                        // Trigger Terra Connect for Apple Health
                        setTriggerTerraConnect(true);
                      }}
                      className="flex-1 px-4 py-4 text-sm font-medium text-base-content bg-base-200 hover:bg-neutral hover:text-white rounded-md transition-colors"
                    >
                      Done, proceed to next step.
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Coming Soon Modal for device limit */}
        {showComingSoonModal && (
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowComingSoonModal(false)}
          >
            <div onClick={(e) => e.stopPropagation()}>
              <ComingSoon featureKey="additionalWearables" showButton={true} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WearableSettings;
